"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "TargetHasher", {
    enumerable: true,
    get: function() {
        return TargetHasher;
    }
});
const _globhasher = require("glob-hasher");
const _globby = require("@lage-run/globby");
const _fs = /*#__PURE__*/ _interop_require_default(require("fs"));
const _path = /*#__PURE__*/ _interop_require_default(require("path"));
const _workspacetools = require("workspace-tools");
const _infoFromPackageJson = require("workspace-tools/lib/infoFromPackageJson.js");
const _hashStrings = require("./hashStrings.js");
const _resolveInternalDependencies = require("./resolveInternalDependencies.js");
const _resolveExternalDependencies = require("./resolveExternalDependencies.js");
const _FileHasher = require("./FileHasher.js");
const _PackageTree = require("./PackageTree.js");
const _getInputFiles = require("./getInputFiles.js");
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
class TargetHasher {
    getPackageInfos(workspacePackages) {
        const { root } = this.options;
        const packageInfos = {};
        if (workspacePackages.length) {
            for (const pkg of workspacePackages){
                packageInfos[pkg.name] = pkg.packageJson;
            }
        } else {
            const packageJsonPath = _path.default.join(root, "package.json");
            if (_fs.default.existsSync(packageJsonPath)) {
                try {
                    const packageJson = JSON.parse(_fs.default.readFileSync(packageJsonPath, "utf-8"));
                    const rootInfo = (0, _infoFromPackageJson.infoFromPackageJson)(packageJson, packageJsonPath);
                    if (rootInfo) {
                        packageInfos[rootInfo.name] = rootInfo;
                    }
                } catch (e) {
                    throw new Error(`Invalid package.json file detected ${packageJsonPath}: ${e?.message || e}`);
                }
            }
        }
        return packageInfos;
    }
    ensureInitialized() {
        if (!this.initializedPromise) {
            throw new Error("TargetHasher is not initialized");
        }
    }
    async initialize() {
        const { environmentGlob, root } = this.options;
        if (this.initializedPromise) {
            await this.initializedPromise;
            return;
        }
        this.initializedPromise = Promise.all([
            this.fileHasher.readManifest().then(()=>(0, _globby.globAsync)(environmentGlob, {
                    cwd: root
                })).then((files)=>this.fileHasher.hash(files)).then((hash)=>this.globalInputsHash = hash),
            (0, _workspacetools.getWorkspacesAsync)(root).then((workspaceInfo)=>this.workspaceInfo = workspaceInfo).then(()=>{
                this.packageInfos = this.getPackageInfos(this.workspaceInfo);
                this.dependencyMap = (0, _workspacetools.createDependencyMap)(this.packageInfos, {
                    withDevDependencies: true,
                    withPeerDependencies: false
                });
                this.packageTree = new _PackageTree.PackageTree({
                    root,
                    packageInfos: this.packageInfos,
                    // TODO: (optimization) false if process.env.TF_BUILD || process.env.CI
                    includeUntracked: true
                });
                return this.packageTree.initialize();
            }),
            (0, _workspacetools.parseLockFile)(root).then((lockInfo)=>this.lockInfo = lockInfo)
        ]);
        await this.initializedPromise;
        if (this.logger !== undefined) {
            const globalInputsHash = (0, _hashStrings.hashStrings)(Object.values(this.globalInputsHash ?? {}));
            this.logger.verbose(`Global inputs hash: ${globalInputsHash}`);
        }
    }
    async hash(target) {
        this.ensureInitialized();
        const { root } = this.options;
        if (target.cwd === root && target.cache) {
            if (!target.inputs) {
                throw new Error("Root-level targets must have `inputs` defined if it has cache enabled.");
            }
            const files = await (0, _globby.globAsync)(target.inputs, {
                cwd: root
            });
            const fileFashes = (0, _globhasher.hash)(files, {
                cwd: root
            }) ?? {};
            const hashes = Object.values(fileFashes);
            return (0, _hashStrings.hashStrings)(hashes);
        }
        // 1. add hash of target's inputs
        // 2. add hash of target packages' internal and external deps
        const { dependencies, devDependencies } = this.packageInfos[target.packageName];
        const workspaceInfo = this.workspaceInfo;
        const parsedLock = this.lockInfo;
        const allDependencies = {
            ...dependencies,
            ...devDependencies
        };
        const internalDeps = (0, _resolveInternalDependencies.resolveInternalDependencies)(allDependencies, workspaceInfo);
        const externalDeps = (0, _resolveExternalDependencies.resolveExternalDependencies)(allDependencies, workspaceInfo, parsedLock);
        const resolvedDependencies = [
            ...internalDeps,
            ...externalDeps
        ].sort();
        const files = (0, _getInputFiles.getInputFiles)(target, this.dependencyMap, this.packageTree);
        const fileHashes = this.fileHasher.hash(files) ?? {}; // this list is sorted by file name
        // get target hashes
        const targetDepHashes = target.dependencies?.sort().map((targetDep)=>this.targetHashes[targetDep]);
        const globalFileHashes = await this.getEnvironmentGlobHashes(root, target);
        const combinedHashes = [
            // Environmental hashes
            ...Object.values(globalFileHashes),
            `${target.id}|${JSON.stringify(this.options.cliArgs)}`,
            this.options.cacheKey || "",
            // File content hashes based on target.inputs
            ...Object.values(fileHashes),
            // Dependency hashes
            ...resolvedDependencies,
            ...targetDepHashes
        ].filter(Boolean);
        const hashString = (0, _hashStrings.hashStrings)(combinedHashes);
        this.targetHashes[target.id] = hashString;
        this.targetHashesLog[target.id] = {
            fileHashes,
            globalFileHashes
        };
        return hashString;
    }
    writeTargetHashesManifest() {
        for (const [id, { fileHashes, globalFileHashes }] of Object.entries(this.targetHashesLog)){
            const targetHashesManifestPath = _path.default.join(this.targetHashesDirectory, `${id}.json`);
            if (!_fs.default.existsSync(_path.default.dirname(targetHashesManifestPath))) {
                _fs.default.mkdirSync(_path.default.dirname(targetHashesManifestPath), {
                    recursive: true
                });
            }
            _fs.default.writeFileSync(targetHashesManifestPath, JSON.stringify({
                fileHashes,
                globalFileHashes
            }), "utf-8");
        }
    }
    async getEnvironmentGlobHashes(root, target) {
        const globalFileHashes = target.environmentGlob ? this.fileHasher.hash(await (0, _globby.globAsync)(target.environmentGlob ?? [], {
            cwd: root
        })) : this.globalInputsHash ?? {};
        return globalFileHashes;
    }
    async cleanup() {
        this.writeTargetHashesManifest();
        await this.fileHasher.writeManifest();
    }
    constructor(options){
        _define_property(this, "options", void 0);
        _define_property(this, "targetHashesLog", void 0);
        _define_property(this, "targetHashesDirectory", void 0);
        _define_property(this, "logger", void 0);
        _define_property(this, "fileHasher", void 0);
        _define_property(this, "packageTree", void 0);
        _define_property(this, "initializedPromise", void 0);
        _define_property(this, "packageInfos", void 0);
        _define_property(this, "workspaceInfo", void 0);
        _define_property(this, "globalInputsHash", void 0);
        _define_property(this, "lockInfo", void 0);
        _define_property(this, "targetHashes", void 0);
        _define_property(this, "dependencyMap", void 0);
        this.options = options;
        this.targetHashesLog = {};
        this.packageInfos = {};
        this.targetHashes = {};
        this.dependencyMap = {
            dependencies: new Map(),
            dependents: new Map()
        };
        const { root, logger } = options;
        this.logger = logger;
        this.fileHasher = new _FileHasher.FileHasher({
            root
        });
        this.targetHashesDirectory = _path.default.join(root, "node_modules", ".cache", "lage", "hashes");
        if (!_fs.default.existsSync(this.targetHashesDirectory)) {
            _fs.default.mkdirSync(this.targetHashesDirectory, {
                recursive: true
            });
        }
    }
}
